import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import parse from 'html-react-parser';
import { useQueryClient } from 'react-query';
import useManageAcknowledgementSchedules from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useManageAcknowledgementSchedules';
import { NewManualAcknowledgementScheduleRequest } from 'views/App/Portal/Policy/_types/acknowledgements';
import errorHelper from 'helpers/errorHelper';
import { ValidationErrors } from '../_types/errors';
import AcknowledgementScheduleForm from '../_components/AcknowledgementScheduleForm';
import { translate as t } from 'hooks/useTranslations';

const NewAcknowledgementSchedule: React.FC = () => {
  const queryClient = useQueryClient();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [externalErrors, setExternalErrors] = useState<ValidationErrors>({});

  const { create: createManualAcknowledgementSchedule } = useManageAcknowledgementSchedules();

  const handleSubmit = (payload: NewManualAcknowledgementScheduleRequest) => {
    if (!id) return;
    setIsSaving(true);
    createManualAcknowledgementSchedule(
      id,
      payload,
      (_jobId) => {
        setIsSaving(false);
        enqueueSnackbar(
          parse(
            t(`policy.acknowledgementSchedules.new.saveConfirmation`, '', {
              title: payload.title ?? '',
            })
          ),
          {
            variant: 'success',
            autoHideDuration: 6000,
          }
        );
        queryClient.invalidateQueries(['issued_manual_acknowledgment_schedules']);
        navigate(`../${id}/acknowledgement-schedules`);
      },
      (errors) => {
        setIsSaving(false);
        errorHelper.enqueueErrors(errors);
        if (errors.errorDetails) {
          const backendErrors: ValidationErrors = {};
          errors.errorDetails.forEach((error) => {
            if (error.detail.includes('title')) {
              backendErrors.title = error.detail;
            } else if (error.detail.includes('startAt')) {
              backendErrors.startAt = error.detail;
            } else if (error.detail.includes('dueDays')) {
              backendErrors.dueDays = error.detail;
            } else if (error.detail.includes('groups')) {
              backendErrors.groups = error.detail;
            } else if (error.detail.includes('policies')) {
              backendErrors.policies = error.detail;
            }
          });
          setExternalErrors(backendErrors);
        }
      }
    );
  };

  if (!id) return null;

  return (
    <AcknowledgementScheduleForm
      title={t('policy.acknowledgementSchedules.new.title')}
      manualId={id}
      mode='new'
      onCancelHref={`../${id}/acknowledgement-schedules`}
      onSubmit={handleSubmit}
      isSaving={isSaving}
      externalErrors={externalErrors}
    />
  );
};

export default NewAcknowledgementSchedule;
